import AuthAxios from '../../axios';
import type { PaymentMethod } from '../../types';
import { getItem, setItem, USERINFO } from '../localStorage';

export type DanalBillingResponse = {
  success?: boolean;
  card_name?: string;
  customer_uid?: string;
  imp_uid?: string;
  merchant_uid?: string;
  status?: string;
  paid_amount?: number;
  amount?: number;
  error_msg?: string;
  [key: string]: unknown;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readTruthyFlag(value: unknown): boolean {
  return value === true || value === 1 || value === 'true' || value === '1' || value === 'Y' || value === 'y';
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function getPaymentCardCacheKey(): string {
  const user = getItem(USERINFO) as { id?: string | number } | null;
  const userId = user?.id != null ? String(user.id) : 'guest';
  return `app.soundpill.paymentMethodCard:${userId}`;
}

function readCachedPaymentMethod(): PaymentMethod | undefined {
  const cached = getItem(getPaymentCardCacheKey());
  return parsePaymentMethodResponse(cached);
}

function writeCachedPaymentMethod(method: PaymentMethod): void {
  setItem(getPaymentCardCacheKey(), method);
}

function normalizePaymentMethodPayload(payload: unknown): unknown {
  if (typeof payload !== 'string') return payload;

  const trimmed = payload.trim();
  if (!trimmed) return payload;

  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return payload;
  }
}

function parsePaymentMethodRecord(raw: Record<string, unknown>): PaymentMethod | undefined {
  const cardObject = asRecord(raw.card ?? raw.paymentMethod ?? raw.payment_method);
  const cardName = readString(
    raw.cardName ??
      raw.card_name ??
      (typeof raw.card === 'string' ? raw.card : undefined) ??
      cardObject?.cardName ??
      cardObject?.card_name ??
      cardObject?.name,
  );
  const customerUid = readString(
    raw.customerUid ?? raw.customer_uid ?? cardObject?.customerUid ?? cardObject?.customer_uid,
  );
  const isCard =
    readTruthyFlag(raw.isCard ?? raw.is_card ?? cardObject?.isCard ?? cardObject?.is_card) ||
    Boolean(cardName || customerUid);

  if (!isCard) return undefined;

  return {
    isCard: true,
    cardName,
    customerUid,
  };
}

/** GET /paymentMethod/card 응답 — JSON 객체 / JSON 문자열 / 중첩 객체 처리 */
export function parsePaymentMethodResponse(payload: unknown): PaymentMethod | undefined {
  let normalized = normalizePaymentMethodPayload(payload);

  if (typeof normalized === 'string') {
    const cardName = normalized.trim();
    if (!cardName) return undefined;
    return { isCard: true, cardName, customerUid: '' };
  }

  let raw = asRecord(normalized);
  if (!raw) return undefined;

  const direct = parsePaymentMethodRecord(raw);
  if (direct) return direct;

  const nested = asRecord(raw.data ?? raw.result ?? raw.payload);
  if (nested) {
    const nestedParsed = parsePaymentMethodRecord(nested);
    if (nestedParsed) return nestedParsed;
  }

  for (const value of Object.values(raw)) {
    const nestedRecord = asRecord(value);
    if (!nestedRecord) continue;
    const nestedParsed = parsePaymentMethodRecord(nestedRecord);
    if (nestedParsed) return nestedParsed;
  }

  return undefined;
}

export function hasRegisteredPaymentCard(method?: PaymentMethod): boolean {
  return Boolean(method?.isCard || method?.cardName || method?.customerUid);
}

/** POST /v2/payment — PortOne impUid 등록 (카드 최초인증 0원) */
export async function registerPaymentFromDanal(rsp: DanalBillingResponse): Promise<void> {
  const impUid = readString(rsp.imp_uid);
  if (!impUid) return;

  await AuthAxios.post('/payment', {
    impUid,
    amount: readNumber(rsp.paid_amount ?? rsp.amount, 0),
    status: readString(rsp.status) || 'paid',
    currency: 'KRW',
  });
}

/** PUT /v2/paymentMethod/card2 — Swagger: { customerUid } */
export async function savePaymentCardCustomerUid(customerUid: string): Promise<void> {
  await AuthAxios.put('/paymentMethod/card2', { customerUid });
}

export async function fetchRegisteredPaymentCard(): Promise<PaymentMethod | undefined> {
  try {
    const { data } = await AuthAxios.get('/paymentMethod/card', {
      params: { _: Date.now() },
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });

    const parsed = parsePaymentMethodResponse(data);
    if (parsed) {
      writeCachedPaymentMethod(parsed);
      return parsed;
    }

    return readCachedPaymentMethod();
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404) return readCachedPaymentMethod();
    throw error;
  }
}

export async function fetchRegisteredPaymentCardWithRetry(
  maxAttempts = 6,
  delayMs = 500,
): Promise<PaymentMethod | undefined> {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const method = await fetchRegisteredPaymentCard();
    if (method) return method;
    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => window.setTimeout(resolve, delayMs));
    }
  }
  return readCachedPaymentMethod();
}

function buildPaymentMethodFromDanal(customerUid: string, rsp: DanalBillingResponse): PaymentMethod {
  return {
    isCard: true,
    cardName: readString(rsp.card_name),
    customerUid,
  };
}

/**
 * Danal 인증 후 v2 저장:
 * 1. POST /payment (impUid)
 * 2. PUT /paymentMethod/card2 ({ customerUid })
 * 3. GET /paymentMethod/card (빈 응답이면 Danal 결과 + cache 사용)
 */
export async function saveRegisteredPaymentCardFromDanal(
  customerUid: string,
  rsp: DanalBillingResponse,
): Promise<PaymentMethod> {
  const resolvedCustomerUid = readString(rsp.customer_uid) || customerUid;

  await registerPaymentFromDanal(rsp);
  await savePaymentCardCustomerUid(resolvedCustomerUid);

  const saved =
    (await fetchRegisteredPaymentCardWithRetry()) ??
    buildPaymentMethodFromDanal(resolvedCustomerUid, rsp);

  writeCachedPaymentMethod(saved);
  return saved;
}

export function clearCachedPaymentMethod(): void {
  localStorage.removeItem(getPaymentCardCacheKey());
}
